import { useEffect, useRef, useState } from 'react';

/** Roughly how many animation frames a backlog should take to drain: bigger bursts glide faster. */
const DRAIN_FRAMES = 12;
/** ~30 fps is plenty for text and halves the markdown re-renders. */
const FRAME_MS = 32;

/** Next number of visible characters: always moves forward, speeds up with the backlog, never overshoots. */
export function nextShown(shown: number, total: number): number {
  if (shown >= total) return total;
  const backlog = total - shown;
  return Math.min(total, shown + Math.max(2, Math.ceil(backlog / DRAIN_FRAMES)));
}

const reducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Streamed text arrives in bursts (the server coalesces deltas every ~100 ms). This reveals it at a steady,
 * backlog-adaptive pace instead of pasting each burst. Text that is not live when the component mounts
 * (history) is shown at once; a live text keeps gliding until it has caught up, even after the run ended.
 */
export function useSmoothText(text: string, live: boolean): string {
  const [shown, setShown] = useState(() => (live && !reducedMotion() ? 0 : text.length));
  const total = useRef(text.length);
  total.current = text.length;

  // biome-ignore lint/correctness/useExhaustiveDependencies: restart the glide when the text grows
  useEffect(() => {
    if (shown > text.length) {
      setShown(text.length); // the text shrank (replaced): never show more than exists
      return;
    }
    if (shown >= text.length) return;
    if (reducedMotion()) {
      setShown(text.length);
      return;
    }
    let raf = 0;
    let last = 0;
    const tick = (now: number) => {
      if (now - last >= FRAME_MS) {
        last = now;
        setShown((s) => nextShown(s, total.current));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text.length, shown >= text.length]);

  return shown >= text.length ? text : text.slice(0, shown);
}
